import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	LOCALE_ID,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges
} from "@angular/core";

import { FormatSettings } from "@progress/kendo-angular-dateinputs";
import * as _ from "lodash";
import * as moment from "moment";
import { LABEL_SUFFIX, TranslateService } from "../../../translate";
import { KendoDatetimeLocaleService } from "../../services/kendo.datetime.service";
import { IDate } from "../date.range.picker/date.range.picker.interface";

@Component({
	selector:'cp-datetime-extended-picker',
	templateUrl: './datetime.extended.picker.template.html',
	styleUrls: ['./datetime.extended.picker.style.scss'],
	changeDetection:ChangeDetectionStrategy.OnPush
})
export class DateTimeExtendedPickerComponent implements OnInit, OnChanges {
	@Input() date: IDate;
	@Input() dateType: string = 'Date';  //Date, DateTime
	@Input() type: string = 'Fixed';  //Fixed, Relative
	@Input() dateIsMandatory: boolean;
	@Input() startDateForRelativeDate: Date;
	@Input() noStartDateSelectedText: string;

	@Output() onChange: EventEmitter<IDate> = new EventEmitter<IDate>(false);

	public start:number;
	public startScale: { name: string, description: string };
	public scaleTypes:{ name: string, description: string }[] = [];

	public selectedDate: Date;
	public format: FormatSettings;
	public validDate = true;

	public baseDateFactor: Date;
	public startDateFactor:Date;
	public exampleLabel: string;

	public scale: { name: string, description: string }[] = [
		{ name: "Hours", description:  <string>this.translateService.instant('Hours' + LABEL_SUFFIX) },
		{ name: "Days", description: <string>this.translateService.instant('Days' + LABEL_SUFFIX) },
		{ name: "Weeks", description: <string>this.translateService.instant('Weeks' + LABEL_SUFFIX) },
		{ name: "Months", description: <string>this.translateService.instant('Months' + LABEL_SUFFIX) },
		{ name: "Years", description: <string>this.translateService.instant('Years' + LABEL_SUFFIX) }];

	constructor(private cd: ChangeDetectorRef,
				@Inject( LOCALE_ID ) private locale: string,
				private translateService:TranslateService,
				private datetimeFormatService:KendoDatetimeLocaleService) {
		this.baseDateFactor = moment('01/01/0001 00:00:00', "MM/DD/YYYY HH:mm:ss").toDate();
		this.start = 0;
		this.startScale = this.scale[1];
	}

	ngOnChanges(changes: SimpleChanges){
		if (changes["date"]) {
			if (this.date.isRelative) {
				if (this.date.date !== this.startDateFactor) {
					this.type = 'Relative';
					this.startDateFactor = null;
					this.populateRelativeControls();
					this.computeRelativeDate(false);
					this.buildRelativeDatesSimulateLabel();
					this.cd.markForCheck();
				}
			} else {
				this.type = 'Fixed';
				this.selectedDate = this.date.date;
				this.validateDate();
			}
		}

		if (changes["dateType"]) {
			this.buildResolutionTypes();
		}

		if (changes["startDateForRelativeDate"]) {
			this.buildRelativeDatesSimulateLabel();
			this.cd.markForCheck();
		}
	}

	ngOnInit(){
		if (this.dateType === 'DateTime') {
			this.format = this.getDateTimeFormat();
		}
	}

	public onTypeChanged() {
		if (this.type === 'Relative') {
			this.date = {date: this.startDateFactor, isRelative: true};
			this.buildResolutionTypes();
			this.populateRelativeControls();
			this.computeRelativeDate();
			this.buildRelativeDatesSimulateLabel();
			this.cd.markForCheck();
		} else {
			this.onChange.emit({date: this.selectedDate, isRelative: false});
		}
	}

	public computeRelativeDate(fireChangeEvent = true) {
		if (!this.startScale) {
			return;
		}

		this.startDateFactor = _.cloneDeep(this.baseDateFactor);
		if (this.start > 0) {
			switch (this.startScale.name) {
				case 'Hours':
					this.startDateFactor.setHours(this.startDateFactor.getHours() + this.start);
					break;
				case 'Days':
					this.startDateFactor.setDate(this.startDateFactor.getDate() + this.start);
					break;
				case 'Weeks':
					this.startDateFactor.setDate(this.startDateFactor.getDate() + (this.start * 7));
					break;
				case 'Months':
					this.startDateFactor.setMonth(this.startDateFactor.getMonth() + this.start);
					break;
				case 'Years':
					this.startDateFactor.setFullYear(this.startDateFactor.getFullYear() + this.start);
					break;
			}
		}

		if (fireChangeEvent) {
			this.onChange.emit({date: this.startDateFactor, isRelative: true});
		}
	}

	public buildRelativeDatesSimulateLabel() {
		this.exampleLabel = undefined;

		if (!this.startDateForRelativeDate || !this.startDateFactor ) {
			return;
		}

		let startDate = this.computeDateTime(this.startDateForRelativeDate, this.startDateFactor, -1);

		this.exampleLabel = <string>this.translateService.instant('ValuesWillBeComputed' + LABEL_SUFFIX)  + ' ' +
			this.datetimeFormatService.datetime(this.startDateForRelativeDate) + ':' + '<br/>' +
			<string>this.translateService.instant('SelectedDateUpper' + LABEL_SUFFIX) + ': ' +
			(this.dateType === 'Date' ? this.datetimeFormatService.date(startDate) : this.datetimeFormatService.datetime(startDate));

		this.cd.markForCheck();
	}

	public handleSelectionChange($event: Date) {
		this.date.date = $event;
		this.onChange.emit(this.date);
	}

	private validateDate(){
		if (this.dateIsMandatory) {
			this.validDate = !!this.selectedDate;
		} else {
			this.validDate = true;
		}
		this.cd.detectChanges();
	}

	private populateRelativeControls() {
		this.start = 0;
		this.startScale = this.scale[1];

		if (this.date.date) {
			let start = this.parseRelativeDate(this.date.date);
			this.start = start.interval;
			this.startScale = this.scale.find(r => r.name === start.resolution);
		}
	}

	private parseRelativeDate(date: Date) {
		let interval = 0;
		let resolution: string;

		let hours = date.getHours();
		let days = date.getDate() - 1;
		let months = date.getMonth();
		let years = date.getFullYear() - 1;

		if (years > 0) {
			if (months === 0 && days === 0) {
				interval = years;
				resolution = 'Years';
			} else if (months > 0 || days > 0) {
				if (months > 0 && days === 0) {
					interval = years * 12 + months;
					resolution = 'Months';
				} else {
					let totalDays = Math.ceil((date.getTime() - this.baseDateFactor.getTime()) / (1000 * 3600 * 24));
					if (totalDays % 7 === 0) {
						interval = totalDays / 7;
						resolution = 'Weeks';
					} else {
						interval = totalDays;
						resolution = 'Days';
					}
				}
			}
		} else if (months > 0) {
			if (days === 0) {
				interval = months;
				resolution = 'Months';
			} else {
				let totalDays = Math.ceil((date.getTime() - this.baseDateFactor.getTime()) / (1000 * 3600 * 24));
				if (totalDays % 7 === 0) {
					interval = totalDays / 7;
					resolution = 'Weeks';
				} else {
					interval = totalDays;
					resolution = 'Days';
				}
			}
		} else if (days > 0) {
			if (hours === 0) {
				if (days % 7 === 0) {
					interval = days / 7;
					resolution = 'Weeks';
				} else {
					interval = days;
					resolution = 'Days';
				}
			} else {
				let totalHours = Math.ceil((date.getTime() - this.baseDateFactor.getTime()) / (1000 * 3600));
				interval = totalHours;
				resolution = 'Hours';
			}
		} else {
			interval = hours;
			resolution = 'Hours';
		}

		return {interval: interval, resolution: resolution};
	}

	private computeDateTime(baseDate: Date, dateFactor: Date, multiplier: number) {
		let baseDateCloned = _.cloneDeep(baseDate);
		return new Date(new Date(new Date(new Date(new Date(baseDateCloned.
		setFullYear(baseDateCloned.getFullYear() + ((dateFactor.getFullYear() - 1) * multiplier))).
		setMonth(baseDateCloned.getMonth() + ((dateFactor.getMonth()) * multiplier))).
		setDate(baseDateCloned.getDate() + ((dateFactor.getDate() - 1) * multiplier))).
		setHours(baseDateCloned.getHours() + (dateFactor.getHours() * multiplier))).setSeconds(0));
	}

	private buildResolutionTypes() {
		this.scaleTypes = [];
		if (this.dateType === 'DateTime') {
			this.scaleTypes.push(this.scale[0]);
		}
		this.scaleTypes.push(...this.scale.slice(1));
	}

	private getDateTimeFormat(): FormatSettings {
		let format: FormatSettings;

		if (this.locale == 'he-IL') { //the format is set specifically for he-IL because for some reason short format was presented with . and not with /
			format = { inputFormat: { pattern: 'dd/MM/yyyy HH:mm'},  displayFormat: { pattern: 'dd/MM/yyyy HH:mm'}};
		}
		else {
			format = { inputFormat: { datetime: 'short'},  displayFormat: { datetime: 'short'}};
		}

		return format;
	}
}

